import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Stack
} from '@mui/material';
import { useAuth } from '../auth/useAuth';
import { LoadingSpinner } from '../../shared/components';
import UserManagement from './components/UserManagement';
import AuditLogs from './components/AuditLogs';
import EmailTestPanel from './components/EmailTestPanel';
import EmailRecipientManager from './components/EmailRecipientManager';
import StaffPerformanceSyncCard from './components/StaffPerformanceSyncCard';
import SuccessUpdatesSyncCard from './components/SuccessUpdatesSyncCard';
import CoachSchedulesSyncCard from './components/CoachSchedulesSyncCard';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `admin-tab-${index}`,
    'aria-controls': `admin-tabpanel-${index}`,
  };
};

const AdminPanel: React.FC = () => {
  const { userRole } = useAuth();
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; type: 'success' | 'error' }>({
    open: false,
    message: '',
    type: 'success'
  });

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Check if user has admin or manager role
  if (userRole !== 'ADMIN' && userRole !== 'MANAGER') {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Admin Panel
        </Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>
          You do not have permission to access the Admin Panel. This page is only accessible to administrators and managers.
        </Alert>
      </Box>
    );
  }

  // Define tabs based on role
  const tabs = userRole === 'ADMIN' 
    ? ['User Management', 'Audit Logs', 'Email Test', 'Email Recipients', 'Data Sync']
    : ['Audit Logs'];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Admin Panel
      </Typography>
      
      <Paper sx={{ width: '100%', mt: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={value} 
            onChange={handleChange} 
            aria-label="admin panel tabs"
          >
            {tabs.map((label, index) => (
              <Tab key={label} label={label} {...a11yProps(index)} />
            ))}
          </Tabs>
        </Box>
        
        <Box sx={{ px: 3 }}>
          {userRole === 'ADMIN' ? (
            <>
              <TabPanel value={value} index={0}>
                <UserManagement 
                  setLoading={setLoading}
                  setError={setError}
                  setSnackbar={setSnackbar}
                />
              </TabPanel>
              <TabPanel value={value} index={1}>
                <AuditLogs 
                  setLoading={setLoading}
                  setError={setError}
                  setSnackbar={setSnackbar}
                />
              </TabPanel>
              <TabPanel value={value} index={2}>
                <EmailTestPanel />
              </TabPanel>
              <TabPanel value={value} index={3}>
                <EmailRecipientManager />
              </TabPanel>
              <TabPanel value={value} index={4}>
                <Stack spacing={3}>
                  <StaffPerformanceSyncCard />
                  <SuccessUpdatesSyncCard />
                  <CoachSchedulesSyncCard />
                </Stack>
              </TabPanel>
            </>
          ) : (
            <TabPanel value={value} index={0}>
              <AuditLogs 
                setLoading={setLoading}
                setError={setError}
                setSnackbar={setSnackbar}
              />
            </TabPanel>
          )}
        </Box>
      </Paper>
      
      {loading && (
        <LoadingSpinner fullScreen />
      )}
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbar.message}
      />
    </Box>
  );
};

export default AdminPanel;
